import { Button, Popconfirm } from 'antd'
import { observer } from 'mobx-react-lite'
import { useRef, useState, type DragEvent } from 'react'
import { MdOutlineFileUpload, MdPhotoCamera } from 'react-icons/md'

import { useCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { UserAvatarContent } from '@/Ship/Components/UI/UserPhoto/UserAvatarContent'
import photoStyles from '@/Ship/Components/UI/UserPhoto/UserPhoto.module.css'
import { usePhotoCropPicker } from '@/Ship/Components/UI/UserPhoto/usePhotoCropPicker'
import { cx } from '@/Ship/Helpers/cssClass'
import { notify } from '@/Ship/Helpers/notify'
import { parseApiErrorDescription } from '@/Ship/Helpers/parseApiError'
import { PHOTO_MAX_SOURCE_MB, resolveUserPhotoUrl } from '@/Ship/Helpers/userPhoto'
import { UploadUserPhotoTask } from '@/Ship/Tasks/UserPhotoTasks'
import { resolveInitials } from '@/Ship/Templates/Layouts/Components/UserProfileBlock/UserProfileBlock'

import styles from './UserPhotoField.module.css'

const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes('Files')

interface UserPhotoFieldProps {
    formContext: any
}

/**
 * «Фотографія» у редагуванні картки співробітника: аватар (клік або перетягування —
 * змінити), «Завантажити/Змінити фото», «Видалити фото». Вибір, перевірка й обрізання —
 * як у «Моєму профілі» (спільний usePhotoCropPicker). Відмінність: обрізане фото лише
 * завантажується і стає значенням поля `photo`, а до користувача прив'язується разом
 * зі збереженням картки — як і завантаження через стандартне поле file.
 */
export const UserPhotoField = observer(({ formContext }: UserPhotoFieldProps) => {
    const crudContext = useCrudResourceContext() as any
    const label = crudContext?.resource?.fields?.photo?.label ?? 'Фотографія'
    const photo = formContext.values?.photo
    const hasPhoto = resolveUserPhotoUrl(photo) !== null
    const initials = resolveInitials(String(formContext.values?.fullName ?? ''))

    const [dragOver, setDragOver] = useState(false)
    // dragenter/dragleave спрацьовують і на дочірніх елементах — рахуємо глибину
    const dragDepth = useRef(0)

    const picker = usePhotoCropPicker({
        inputTestId: 'user-card-photo-input',
        onSave: async (blob) => {
            try {
                const uploaded = await UploadUserPhotoTask(blob, 'avatar.jpg')
                formContext.setValue('photo', uploaded)
                return true
            } catch (error) {
                const description = await parseApiErrorDescription(error, '')
                notify.error('Не вдалося завантажити фото', description ? { description } : undefined)
                return false
            }
        },
    })

    const changeLabel = hasPhoto ? 'Змінити фото' : 'Завантажити фото'

    const onDragEnter = (event: DragEvent<HTMLButtonElement>) => {
        if (!hasFiles(event)) return
        event.preventDefault()
        dragDepth.current += 1
        setDragOver(true)
    }

    const onDragLeave = () => {
        dragDepth.current = Math.max(0, dragDepth.current - 1)
        if (dragDepth.current === 0) setDragOver(false)
    }

    const onDrop = (event: DragEvent<HTMLButtonElement>) => {
        event.preventDefault()
        dragDepth.current = 0
        setDragOver(false)
        void picker.accept(event.dataTransfer.files?.[0])
    }

    return (
        <div className="form-field form-field-default">
            <span className="field-label">{label}</span>
            <div className={cx(styles, 'row')}>
                <button
                    type="button"
                    className={`${photoStyles['changer']}${dragOver ? ` ${photoStyles['changerActive']}` : ''}${picker.busy ? ` ${photoStyles['changerBusy']}` : ''}`}
                    aria-label={changeLabel}
                    title={changeLabel}
                    disabled={picker.busy}
                    onClick={picker.pick}
                    onDragEnter={onDragEnter}
                    onDragOver={(event) => hasFiles(event) && event.preventDefault()}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                >
                    <span className={cx(styles, 'photo')} aria-hidden="true">
                        <UserAvatarContent photo={photo} initials={initials} />
                    </span>
                    <span className={photoStyles['changerOverlay']} aria-hidden="true">
                        <MdPhotoCamera />
                        {dragOver ? 'Відпустіть тут' : 'Змінити'}
                    </span>
                </button>
                <div className={cx(styles, 'meta')}>
                    <div className={cx(styles, 'actions')}>
                        <Button icon={<MdOutlineFileUpload />} loading={picker.busy} onClick={picker.pick}>
                            {changeLabel}
                        </Button>
                        {hasPhoto && (
                            <Popconfirm
                                title="Видалити фото?"
                                okText="Видалити"
                                okButtonProps={{ danger: true }}
                                cancelText="Скасувати"
                                onConfirm={() => formContext.setValue('photo', null)}
                            >
                                <Button type="text" danger disabled={picker.busy}>
                                    Видалити фото
                                </Button>
                            </Popconfirm>
                        )}
                    </div>
                    <p className={cx(styles, 'hint')}>
                        JPG, PNG, WEBP або HEIC (iPhone) до {PHOTO_MAX_SOURCE_MB} МБ. Фото обрізається у квадрат; зміни
                        збережуться разом із карткою.
                    </p>
                </div>
            </div>
            {picker.holder}
        </div>
    )
})
